import {
  getTablePermissions,
  getIgnoredColumnsByPermission,
  filterColumnsByPermission,
  hasPermission,
} from "@/lib/permissions";
import { SummaryEntry } from "@/components/Table/props";
import type { UserRoleType } from "@/types/auth";
import { router } from "expo-router";
import { PAYROLL_ITEM_STATUS_OPTIONS } from "@/types/db/payroll_items";

const BILLING_STATUS_OPTIONS = [
  { value: "PENDING", color: "neutral" },
  { value: "READY FOR BILLING", color: "todo" },
  { value: "BILLING IN PROGRESS", color: "pending" },
  { value: "PNL PENDING", color: "complete" },
] as const;

const PROJECT_STATUS_OPTIONS = [
  { value: "POTENTIAL", color: "todo" },
  { value: "PENDING APPROVAL", color: "pending" },
  { value: "IN PROGRESS", color: "pending" },
  { value: "ON HOLD", color: "attention" },
  { value: "COMPLETED", color: "complete" },
  { value: "CONFLICTING", color: "attention" },
  { value: "LOST", color: "neutral" },
  { value: "CANCELED", color: "attention" },
] as const;

// Columns that require read permission on related entities
const RELATIONSHIP_COLUMNS = {
  client: "clients",
  client_id: "clients",
  project: "projects",
  project_status: "projects",
  project_billing_status: "projects",
  shift: "shifts",
  position: "positions",
  position_name: "positions",
  worker: "users",
} as const;

export const getPayrollItemTableConfig = (userRoles: UserRoleType[] | null, displayNames) => {
  const permissions = getTablePermissions(userRoles, "payroll");

  // Get columns to hide based on relationship permissions
  const permissionIgnored = getIgnoredColumnsByPermission(userRoles, RELATIONSHIP_COLUMNS);

  // Build readonly array conditionally based on permissions
  const readonlyColumns: (string | [string, (key: string, value: any, row: any) => void])[] = [
    "id",
    "created_at",
    "amount",
  ];

  // Only add clickable client column if user has clients.read
  if (hasPermission(userRoles, "clients", "read")) {
    readonlyColumns.push([
      "client",
      (_key, _value, row) => row.client && router.setParams({ panel: "client", id: row.client.id }),
    ]);
  }

  // Only add clickable worker column if user has users.read
  if (hasPermission(userRoles, "users", "read")) {
    readonlyColumns.push([
      "worker",
      (_key, _value, row) => row.worker && router.setParams({ panel: "user", id: row.worker.id }),
    ]);
  }

  // Only add clickable project column if user has projects.read
  if (hasPermission(userRoles, "projects", "read")) {
    readonlyColumns.push([
      "project",
      (_key, _value, row) => row.project && router.setParams({ panel: "project", id: row.project.id }),
    ]);
  }

  // Only add clickable shift column if user has shifts.read
  if (hasPermission(userRoles, "shifts", "read")) {
    readonlyColumns.push([
      "shift",
      (_key, _value, row) => row.shift && router.setParams({ panel: "shift", id: row.shift.id }),
    ]);
  }

  // Add non-clickable readonly columns
  readonlyColumns.push("status");
  readonlyColumns.push("project_status");
  readonlyColumns.push("project_billing_status");
  readonlyColumns.push("iso_week");
  readonlyColumns.push("start_time");
  readonlyColumns.push("end_time");
  readonlyColumns.push("rate");
  readonlyColumns.push("quantity");

  return {
    entityName: "Payroll Item",
    entityNamePlural: "Payroll Items",

    tableConfig: {
      headerFunc: (name: string) => {
        switch (name) {
          case "id":
            return "Payroll Item Id";
          case "client":
            return displayNames?.clients?.name ?? "Client";
          case "worker":
            return displayNames?.users?.name ?? "Worker";
          case "project":
            return displayNames?.projects?.name ?? "Project";
          case "project_status":
            return `${displayNames?.projects?.name ?? "Project"} Status`;
          case "project_billing_status":
            return `${displayNames?.projects?.name ?? "Project"} Billing Status`;
          case "shift":
            return displayNames?.shifts?.name ?? "Shift";
          case "unit":
            return "Type";
          default:
            return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        }
      },
      ignore: ["tenant_id", "client_id", "position_name", ...permissionIgnored],
      readonly: readonlyColumns,
      validators: {},
      writeKeys: {
        position: "meta_data",
        unit: "meta_data",
      },
      cellTextCallback: (value: any, key: string) => {
        if (value == null) return "";
        if (key === "id") return value.toString().slice(0, 6).toUpperCase();
        if (key === "client_id") return value.toString().slice(0, 6).toUpperCase();
        if (key === "unit") {
          const d = typeof value === "object" ? value : {};
          return d.unit_override || d.unit || "—";
        }
        if (key === "rate") {
          const d = typeof value === "object" ? value : {};
          return d.rate_override || d.rate || "—";
        }
        if (key === "quantity") {
          const d = typeof value === "object" ? value : {};
          const qty = d.quantity_override != null ? d.quantity_override : d.quantity;
          if (qty == null) return "—";
          return typeof qty === "number" ? qty.toFixed(2) : `${qty}`;
        }
        if (key === "amount") {
          const d = typeof value === "object" ? value : {};
          const rateStr = d.rate_override || d.rate || "";
          const qty = d.quantity_override != null ? parseFloat(d.quantity_override) : d.quantity || 0;
          const rateMatch = rateStr.match(/\$?([\d.]+)/);
          const rate = rateMatch ? parseFloat(rateMatch[1]) : 0;
          const amount = rate * qty;
          return amount > 0 ? `$${amount.toFixed(2)}` : "—";
        }
        if (key === "position") {
          const posData =
            typeof value === "string"
              ? (() => {
                  try {
                    return JSON.parse(value);
                  } catch {
                    return {};
                  }
                })()
              : value || {};
          return posData.position_name_override || posData.position_name || "";
        }
        return typeof value === "string" ? value : JSON.stringify(value);
      },
      summary: [
        [
          "amount",
          (data) => {
            const total = data.reduce((sum, item) => {
              const d = item.amount || {};
              const rateStr = d.rate_override || d.rate || "";
              const qty = d.quantity_override != null ? parseFloat(d.quantity_override) : d.quantity || 0;
              const rateMatch = rateStr.match(/\$?([\d.]+)/);
              const rate = rateMatch ? parseFloat(rateMatch[1]) : 0;
              return sum + rate * qty;
            }, 0);
            return `$${total.toFixed(2)}`;
          },
        ],
        [
          "quantity",
          (data) => {
            const total = data.reduce((sum, item) => {
              const d = item.quantity || {};
              const qty = d.quantity_override != null ? parseFloat(d.quantity_override) : d.quantity || 0;
              return sum + qty;
            }, 0);
            return total.toFixed(2);
          },
        ],
        [
          "client_id",
          (data) => {
            const uniqueClients = new Set(data.map((item) => item.client_id).filter(Boolean));
            return `${uniqueClients.size} clients`;
          },
        ],
      ] as SummaryEntry[],
      dataTypes: {
        created_at: "date" as const,
        updated_at: "date" as const,
        client: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        worker: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        shift: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        project: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        status: {
          type: "enum" as const,
          options: PAYROLL_ITEM_STATUS_OPTIONS,
        },
        project_status: {
          type: "enum" as const,
          options: PROJECT_STATUS_OPTIONS,
        },
        project_billing_status: {
          type: "enum" as const,
          options: BILLING_STATUS_OPTIONS,
        },
        position: {
          type: "override_string" as const,
          originalKey: "position_name",
          overrideKey: "position_name_override",
        },
        rate: {
          type: "override_string" as const,
          originalKey: "rate",
          overrideKey: "rate_override",
        },
        quantity: {
          type: "override_string" as const,
          originalKey: "quantity",
          overrideKey: "quantity_override",
        },
        unit: {
          type: "override_string" as const,
          originalKey: "unit",
          overrideKey: "unit_override",
        },
        start_time: "date-time" as const,
        end_time: "date-time" as const,
        iso_week: "string" as const,
      },
    },

    filterChoices: filterColumnsByPermission(
      userRoles,
      [
        { name: "id", kind: "text" as const, displayName: "Payroll Item Id", menus: ["filter", "visible"] as const },
        {
          name: "client",
          displayName: `${displayNames?.clients?.name ?? "Client"}`,
          kind: "linked_record" as const,
          menus: ["filter", "visible", "groupby", "orderby"] as const,
          linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
        },
        {
          name: "worker",
          displayName: `${displayNames?.users?.name ?? "Worker"}`,
          kind: "linked_record" as const,
          menus: ["filter", "visible", "groupby", "orderby"] as const,
          linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
        },
        {
          name: "shift",
          displayName: displayNames?.shifts?.name ?? "Shift",
          kind: "linked_record" as const,
          menus: ["filter", "visible", "groupby", "orderby"] as const,
          linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
        },
        {
          name: "project",
          displayName: displayNames?.projects?.name ?? "Project",
          kind: "linked_record" as const,
          menus: ["filter", "visible", "groupby", "orderby"] as const,
          linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
        },
        { name: "position", kind: "text" as const, menus: ["filter", "visible"] as const },
        {
          name: "status",
          kind: "enum" as const,
          options: [...PAYROLL_ITEM_STATUS_OPTIONS],
          menus: ["filter", "visible", "groupby", "orderby"] as const,
        },
        {
          name: "project_status",
          displayName: `${displayNames?.projects?.name ?? "Project"} Status`,
          kind: "enum" as const,
          options: [...PROJECT_STATUS_OPTIONS],
          menus: ["filter", "visible", "groupby", "orderby"] as const,
        },
        {
          name: "project_billing_status",
          displayName: `${displayNames?.projects?.name ?? "Project"} Billing Status`,
          kind: "enum" as const,
          options: [...BILLING_STATUS_OPTIONS],
          menus: ["filter", "visible", "groupby", "orderby"] as const,
        },
        {
          name: "iso_week",
          kind: "iso_week" as const,
          displayName: "ISO Week",
          menus: ["filter", "visible", "groupby", "orderby"] as const,
        },
        {
          name: "start_time",
          kind: "date" as const,
          displayName: "Start Time",
          menus: ["filter", "visible", "orderby"] as const,
        },
        {
          name: "end_time",
          kind: "date" as const,
          displayName: "End Time",
          menus: ["filter", "visible", "orderby"] as const,
        },
        { name: "created_at", kind: "date" as const, menus: ["filter", "visible", "orderby"] as const },
      ],
      RELATIONSHIP_COLUMNS,
    ),

    ...permissions,
    storageKey: "payroll_items_flyout",
  };
};
